import { useState } from 'react'
import { followUpApi } from 'services/results/follow-up'
import { useNotification } from 'lib/context/notification.context'

export interface FollowUpSummary {
  completed: number
  pending: number
  completed_measurements: number
  last_completed_date: string | null
  categories: Array<{ category_id: number; label: string; completed: number; pending: number }>
  retryable_email_ids: number[]
}

export const lastCompletedMeasurement = (summary?: FollowUpSummary) => {
  if (!summary) return 'No disponible'
  if (summary.last_completed_date) {
    const [year, month, day] = summary.last_completed_date.slice(0, 10).split('-')
    return `${day}/${month}/${year.slice(-2)}`
  }
  return summary.completed_measurements ? 'Fecha no registrada' : 'Sin mediciones completadas'
}

export default function ParticipantFollowUpSummary({
  summary,
  processId,
  onRefresh,
}: {
  summary?: FollowUpSummary
  processId: number
  onRefresh: () => void
}) {
  const [busy, setBusy] = useState(false)
  const { getError, getSuccess, getWarning } = useNotification()
  if (!summary) return <span>No disponible</span>
  const total = summary.completed + summary.pending
  const retry = async () => {
    if (busy) return
    setBusy(true)
    try {
      const result = await followUpApi(processId, 'retry-email', { user_test_ids: summary.retryable_email_ids })
      if (result.some((item: any) => item.send_mail === false))
        getWarning('Hay correos que todavía no se pudieron entregar.')
      else getSuccess('Se actualizó el estado de entrega de los correos.')
    } catch {
      getError('No se pudo reintentar la entrega del correo.')
    } finally {
      setBusy(false)
      onRefresh()
    }
  }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 6,
        fontSize: 13,
        whiteSpace: 'normal',
      }}
      title={summary.categories
        .map((category) => `${category.label}: ${category.completed} completados, ${category.pending} pendientes`)
        .join('\n')}
    >
      {total ? (
        <>
          <span style={{ color: '#21694e' }}>
            {summary.completed} {summary.completed === 1 ? 'completado' : 'completados'}
          </span>
          <span style={{ color: summary.pending ? '#81571a' : '#526b7d', fontWeight: summary.pending ? 600 : 400 }}>
            {summary.pending} {summary.pending === 1 ? 'pendiente' : 'pendientes'}
          </span>
        </>
      ) : (
        <span style={{ color: '#526b7d' }}>Sin Follow Up</span>
      )}
      {summary.retryable_email_ids.length > 0 && (
        <button
          type="button"
          disabled={busy}
          onClick={retry}
          style={{
            font: 'inherit',
            cursor: busy ? 'wait' : 'pointer',
            color: '#2d749c',
            border: '1px solid #b7d0df',
            background: 'white',
            padding: '5px 8px',
            borderRadius: 8,
          }}
        >
          {busy ? 'Reintentando correo…' : 'Reintentar correo'}
        </button>
      )}
    </div>
  )
}
